import {
	type InformationGatheringDraft,
	type InformationGatheringPatientIdentity,
	type InformationGatheringRole,
} from '@feature/information-gathering/information-gathering.model';
import {
	createCompletedInformationGatheringState,
	type InformationGatheringEntry,
} from '@feature/information-gathering/information-gathering-navigation';
import { useInformationGathering } from '@feature/information-gathering/use-information-gathering';
import { InformationGatheringSexAssignedAtBirth } from '@visiba/api-vcs-client-v2';
import { useCallback, useMemo } from 'react';
import { useCreateNewMessage } from './use-create-new-message';

const EMPTY_PATIENT_IDENTITY: InformationGatheringPatientIdentity = { dateOfBirth: '', nhsNumber: '' };

export function useMessagingInformationGathering() {
	const informationGathering = useInformationGathering();
	const { goToScreen } = useCreateNewMessage();

	const continueAfterRespondent = useCallback(
		async (
			role: InformationGatheringRole,
			patientIdentity: InformationGatheringPatientIdentity = EMPTY_PATIENT_IDENTITY,
			options: { replace?: boolean } = {},
		) => {
			const result = await informationGathering.begin({
				role,
				patientIdentity,
				// This route is the UK pregnancy fallback when RedRobin cannot triage.
				preselectedSexAssignedAtBirth: InformationGatheringSexAssignedAtBirth.Female,
				destination: 'messaging',
			});
			switch (result.kind) {
				case 'bypass':
					goToScreen('reason-for-visit', options);
					break;
				case 'collect':
					goToScreen('information-gathering', { ...options, state: result.state });
					break;
				case 'blocked':
					goToScreen(result.reason === 'nhs-login-required' ? 'nhs-login-required' : 'error', { replace: true });
					break;
			}
		},
		[goToScreen, informationGathering],
	);

	const complete = useCallback(
		async (
			entry: InformationGatheringEntry,
			draft: InformationGatheringDraft,
			patientIdentity: InformationGatheringPatientIdentity,
		): Promise<boolean> => {
			const submissionId = await informationGathering.submit(entry, draft, patientIdentity);
			if (submissionId === null) return false;

			goToScreen('reason-for-visit', {
				replace: true,
				state: createCompletedInformationGatheringState({ submissionId }),
			});
			return true;
		},
		[goToScreen, informationGathering],
	);

	return useMemo(() => ({ continueAfterRespondent, complete }), [complete, continueAfterRespondent]);
}
